/*!

=========================================================
* Argon Design System React - v1.1.2
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-design-system-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-design-system-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import React, { useEffect, useRef, useState } from "react";

// reactstrap components
// The tab machinery is still reactstrap's. The cards, fields and buttons are
// brand-auth primitives now — see styles/brand-auth.css.
import {
  Container,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import "./pages.css";
import "../../styles/brand-auth.css";



// core components
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import MainFooter from "components/Footers/MainFooter.js";
import { useUserAuth } from "../../context/UserContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classnames from "classnames";
import {
  editAccountService,
  getUserPortal,
  requestSubscriptionSummary,
  requestDesktopCredits,
  requestCreditPackCheckout,
} from "../../services/userService"
import { getFreshIdToken } from "../../utils/authToken.js"
import useReveal from "hooks/useReveal";
import { studioApi } from "../../services/studioService";
import { StudioHostingPanel } from "../../components/Studio/StudioPublish";
import StudioPlanPanel from "../../components/Studio/StudioPlanPanel";
import { formatPlanPrice } from "../../components/Studio/studioText";
import DeleteAccountSection from "../../components/AccountSettings/DeleteAccountSection";
import { EmailAuthProvider, reauthenticateWithCredential } from "firebase/auth";
import { auth } from "../../config/firebase-config";

// The Desktop credits tab is hidden: credits are not sold any more, and Studio
// stopped reading them (docs/studio-plans-spec.md, W-7). The code stays,
// behind this switch.
const SHOW_DESKTOP_CREDITS = false;

/**
 * Credits are whole cents of Codenec-charged cost, so they are shown as money,
 * with the currency code ("USD 25.00"): a bare "$" reads as pesos to much of
 * the Spanish-speaking audience.
 */
const formatCredits = (credits) => formatPlanPrice({ amount: Number(credits) || 0, currency: "usd" });

/** Stripe amounts are in the smallest currency unit. */
const formatMoney = (amount, currency) =>
  formatPlanPrice({ amount: Number(amount) || 0, currency: String(currency || "usd") });

// The tab rail, in order. Credits only while SHOW_DESKTOP_CREDITS is on.
const TABS = [
  { id: "1", name: "account" },
  { id: "2", name: "subscription" },
  { id: "3", name: "security" },
  ...(SHOW_DESKTOP_CREDITS ? [{ id: "4", name: "credits" }] : []),
];

const AccountSettingsComponent = () => {

  // The panel's entrance — see styles/premium.css.
  useReveal();

  const { t } = useTranslation();
  const { setUser, user, reauthenticate, changePassword, reloadAuthUser } = useUserAuth();
  const test = "test";
  const mainRef = useRef(null);
  // Focus target when "Go to my subscription" switches tabs from the Delete
  // account section (see onGoToBilling below).
  const subscriptionTitleRef = useRef(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  // A cancelled credit checkout returns to ?credits=canceled. Landing on the
  // Account tab after backing out of a purchase reads as though the click did
  // nothing, so open the tab the purchase started from.
  // ?tab=billing opens the Subscription tab (the Studio's links, and the
  // Studio billing portal's return_url).
  const [activeTab, setActiveTab] = useState(() =>
    SHOW_DESKTOP_CREDITS && searchParams.has("credits") ? '4' : searchParams.get("tab") === "billing" ? '2' : '1'
  );
  const tabRefs = useRef({});
  const [allowEdit, setAllowEdit] = useState(false);
  const [reloadTrigger, setReloadTrigger] = useState(0);
  const [subInfo, setSubInfo] = useState();
  const [haveSub, setHaveSub] = useState(false);
  // Studio hosting plans still billing, each with its own Cancel. Listed here
  // as well as on each app's Publish tab, because this page stays reachable
  // even when the client's Studio access has been switched off.
  const [hostingPlans, setHostingPlans] = useState([]);

  // Desktop credits. `credits` is null until the one backend read answers -
  // there is nothing sensible to render from a guess, and a balance that
  // starts at $0.00 and jumps is worse than one that arrives once.
  const [credits, setCredits] = useState(null);
  const [creditsError, setCreditsError] = useState("");
  const [creditsLoading, setCreditsLoading] = useState(true);
  const [buyingPack, setBuyingPack] = useState("");

  // Every form on this page gets its own error/success pair. One shared banner
  // would show a password error while the user is looking at the Account tab.
  const [accountError, setAccountError] = useState("");
  const [accountMessage, setAccountMessage] = useState("");

  const [emailForm, setEmailForm] = useState({ newEmail: "", currentPassword: "" });
  const [emailError, setEmailError] = useState("");
  const [emailMessage, setEmailMessage] = useState("");
  const [emailSaving, setEmailSaving] = useState(false);

  const [passwordForm, setPasswordForm] = useState({ currentPassword: "", newPassword: "", confirmPassword: "" });
  const [passwordError, setPasswordError] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);


  const [state, setState] = useState({
    name: "",
    last: "",
  });






  const toggle = tab => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  // The courses page's tab pattern: arrow keys, Home and End move between the
  // tabs and select them; only the selected tab is a Tab stop.
  const onTabKeyDown = (e) => {
    const ids = TABS.map((tab) => tab.id);
    const i = Math.max(ids.indexOf(activeTab), 0);
    const next = {
      ArrowDown: ids[(i + 1) % ids.length],
      ArrowRight: ids[(i + 1) % ids.length],
      ArrowUp: ids[(i - 1 + ids.length) % ids.length],
      ArrowLeft: ids[(i - 1 + ids.length) % ids.length],
      Home: ids[0],
      End: ids[ids.length - 1],
    }[e.key];
    if (!next) return;
    e.preventDefault();
    toggle(next);
    tabRefs.current[next]?.focus();
  };



  useEffect(() => {

    subscriptionSummaryMethod();
    loadHostingPlans();
    // Fetched on mount rather than when the tab opens: the balance is the
    // thing a client comes to this page to check, and a spinner that starts
    // when they click the tab makes it feel slower than it is.
    if (SHOW_DESKTOP_CREDITS) loadCredits();

  }, []);


  useEffect(() => {
    if (user) {
      setState({
        name: user.name || "",
        last: user.last || "",
      });

    }

    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
    mainRef.current.scrollTop = 0;


  }, [reloadTrigger]);


  const handleChange = (event) => {
    setState({ ...state, [event.target.name]: event.target.value });
  };

  const hangleEdit = () => setAllowEdit(true);

  // Account tab: profile fields only. Email and password moved to the Security
  // tab, where both are gated behind the current password.
  const handleSubmit = async (event) => {
    event.preventDefault();
    setAccountError("");
    setAccountMessage("");

    const { name, last } = state;

    if (!name.trim()) {
      setAccountError("Please enter your name.");
      return;
    }

    try {
      const token = await getFreshIdToken(user?.accessToken);
      const responseUser = await editAccountService({ name, last, token });

      setUser(responseUser);
      setAllowEdit(false);
      setReloadTrigger(prev => prev + 1);
      setAccountMessage("Your details were saved.");
    } catch (error) {
      setAccountError(getAccountErrorMessage(error));
    }
  };


  const handleEmailChange = (event) => {
    setEmailForm({ ...emailForm, [event.target.name]: event.target.value });
  };


  const handleEmailSubmit = async (event) => {
    event.preventDefault();
    setEmailError("");
    setEmailMessage("");

    const newEmail = emailForm.newEmail.trim();

    if (!newEmail) {
      setEmailError("Enter the new email address.");
      return;
    }
    if (newEmail.toLowerCase() === (user?.email || "").toLowerCase()) {
      setEmailError("That is already your email address.");
      return;
    }
    if (!emailForm.currentPassword) {
      setEmailError("Enter your current password to confirm the change.");
      return;
    }

    setEmailSaving(true);
    try {
      await reauthenticate(emailForm.currentPassword);

      const token = await getFreshIdToken(user?.accessToken);
      const responseUser = await editAccountService({ email: newEmail, changeEmail: true, token });

      // The change is done from here on. The steps below only refresh this
      // browser's sign-in, so a failure in them is logged, never shown as
      // "your email was not changed".
      //
      // The backend refuses tokens issued before an account change (it checks
      // for revoked tokens), and the Admin SDK change may revoke the ones this
      // browser holds. Signing in again with the NEW address and the password
      // just typed mints fresh tokens before anything else calls the backend.
      // It has to come before the reload, which itself needs a valid token.
      try {
        await reauthenticateWithCredential(
          auth.currentUser,
          EmailAuthProvider.credential(newEmail, emailForm.currentPassword)
        );
      } catch (reauthError) {
        console.error("Re-sign-in after the email change failed:", reauthError);
      }
      // The Admin SDK made the change server-side, so refresh the client's
      // cached profile before anything else reads auth.currentUser.email.
      try {
        await reloadAuthUser();
      } catch (reloadError) {
        console.error("Reloading the signed-in user after the email change failed:", reloadError);
      }

      setUser(responseUser);
      setEmailForm({ newEmail: "", currentPassword: "" });
      setReloadTrigger(prev => prev + 1);
      setEmailMessage("Your email address was updated. Use it next time you log in.");
    } catch (error) {
      setEmailError(getAccountErrorMessage(error));
    } finally {
      setEmailSaving(false);
    }
  };


  const handlePasswordChange = (event) => {
    setPasswordForm({ ...passwordForm, [event.target.name]: event.target.value });
  };


  const handlePasswordSubmit = async (event) => {
    event.preventDefault();
    setPasswordError("");
    setPasswordMessage("");

    const { currentPassword, newPassword, confirmPassword } = passwordForm;

    if (!currentPassword) {
      setPasswordError("Enter your current password.");
      return;
    }
    if (newPassword.length < 6) {
      setPasswordError("Your new password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError("The new passwords do not match.");
      return;
    }
    if (newPassword === currentPassword) {
      setPasswordError("Your new password must be different from your current one.");
      return;
    }

    setPasswordSaving(true);
    try {
      await changePassword(currentPassword, newPassword);

      // A password change revokes the tokens issued before it, and the
      // backend now refuses revoked tokens. Signing in again with the NEW
      // password gives this browser tokens issued after the change. The
      // password is already changed, so a failure here is only logged.
      try {
        await reauthenticate(newPassword);
      } catch (reauthError) {
        console.error("Re-sign-in after the password change failed:", reauthError);
      }

      setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      setPasswordMessage("Your password was updated.");
    } catch (error) {
      setPasswordError(getAccountErrorMessage(error));
    } finally {
      setPasswordSaving(false);
    }
  };


  // Firebase codes arrive from two directions here: straight off the client SDK
  // (reauth, updatePassword) as error.code, and relayed by /codenec/edit-account
  // as response.data.code. Read both.
  const getAccountErrorMessage = (error) => {
    const code = error?.code || error?.response?.data?.code;

    switch (code) {
      // Newer Firebase projects collapse a wrong password into
      // invalid-credential so the response cannot confirm which accounts exist.
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return "That is not your current password. Please try again.";

      case "auth/too-many-requests":
        return "Too many attempts. Wait a few minutes and try again.";

      case "auth/weak-password":
        return "Your new password is too weak. Use at least 6 characters.";

      case "auth/email-already-in-use":
      case "auth/email-already-exists":
        return "That email address is already used by another account.";

      case "auth/invalid-email":
        return "That does not look like a valid email address.";

      case "auth/requires-recent-login":
        return "For security, please log out and back in, then try again.";

      case "auth/network-request-failed":
        return "Network error. Check your connection and try again.";

      case "auth/no-current-user":
        return "Your session expired. Please log in again.";

      default:
        return "Something went wrong. Please try again.";
    }
  };

  const requestUserPortal = async () => {
    const portalResponse = await getUserPortal(user.uid, user.email);

    // getUserPortal answers undefined when the request failed; don't send the
    // browser to "undefined".
    if (portalResponse) window.location.href = portalResponse;
  };


  // One call for balance, packs, eligibility and receipts: the tab cannot
  // render without all four, and three round trips would show it arriving in
  // three stages. Every desktop_* collection is closed to the client SDK, so
  // this is the only way the browser can learn any of it.
  const loadCredits = async () => {
    setCreditsLoading(true);
    setCreditsError("");
    try {
      const token = await getFreshIdToken(user?.accessToken);
      setCredits(await requestDesktopCredits(token));
    } catch (error) {
      // 401 is a stale session, anything else is ours. Neither is worth a
      // stack trace in front of a client, but they are different problems.
      setCreditsError(
        error?.response?.status === 401
          ? "Your session expired. Log out and back in to see your credits."
          : "We could not load your credit balance. Try again in a moment."
      );
    } finally {
      setCreditsLoading(false);
    }
  };

  // Sends the browser to Stripe. `packId` is the only thing that travels: the
  // price and the credit count live in the backend catalogue, so nothing here
  // can be edited into a cheaper purchase.
  const buyCreditPack = async (packId) => {
    setBuyingPack(packId);
    setCreditsError("");
    try {
      const token = await getFreshIdToken(user?.accessToken);
      const { url } = await requestCreditPackCheckout(token, packId, "/accountsettings");
      if (!url) throw new Error("no checkout url");
      // assign, not replace: Back from Stripe should return here.
      window.location.href = url;
    } catch (error) {
      setBuyingPack("");
      setCreditsError(
        error?.response?.data?.message ||
        "We could not start the checkout. Try again in a moment."
      );
    }
  };

  const loadHostingPlans = async () => {
    if (!user || user.isAnonymous) return;
    try {
      setHostingPlans(await studioApi.hostingPlans(user));
    } catch (error) {
      // Not a Studio client, or offline: the section just stays hidden.
      setHostingPlans([]);
    }
  };

  const subscriptionSummaryMethod = async () => {
    // Guests (anonymous accounts) have nothing to bill, and the backend now
    // refuses them on billing routes, so don't ask.
    if (user?.isAnonymous) return;

    const subSummaryResponse = await requestSubscriptionSummary(user.uid);
    setSubInfo(subSummaryResponse);

    if(subSummaryResponse?.hasSubscription){
      setHaveSub(true);
    }
  };


  return (
    <>
      <DemoNavbar />
      <main className="cn-cover-page profile-page" ref={mainRef}>

        {/* Navy cover, same as the profile page. Taller, because the panel it
            carries is taller and has to clear the navbar under it. */}
        <section className="cn-cover cn-cover-tall cn-grid">
          <span className="cn-grain" aria-hidden="true" />
          <span className="cn-orb cn-orb-1" />
          <span className="cn-orb cn-orb-2" />
          <span className="cn-orb cn-orb-3" />
        </section>

        <Container className="cn-acct-shell">
          <div className="cn-panel accoundSettingCard" data-reveal>
            <div className="d-flex fullWitdthHeight">
              <Nav
                tabs
                vertical
                className="my-nav-account"
                role="tablist"
                aria-orientation="vertical"
                aria-label={t("accountSettings.tabsLabel")}
                onKeyDown={onTabKeyDown}
              >
                {TABS.map((tab) => (
                  <NavItem key={tab.id} role="presentation">
                    <NavLink
                      role="tab"
                      id={`acct-tab-${tab.name}`}
                      className={classnames({ active: activeTab === tab.id })}
                      aria-selected={activeTab === tab.id}
                      aria-controls={`acct-pane-${tab.name}`}
                      tabIndex={activeTab === tab.id ? 0 : -1}
                      innerRef={(el) => { tabRefs.current[tab.id] = el; }}
                      onClick={() => toggle(tab.id)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          toggle(tab.id);
                        }
                      }}
                    >
                      {tab.name === "credits" ? "Desktop credits" : t(`accountSettings.tabs.${tab.name}`)}
                    </NavLink>
                  </NavItem>
                ))}
              </Nav>

              <TabContent className="fullWitdthHeight cn-acct-body" activeTab={activeTab}>

                {/* ------------------------------- Account ---------------- */}
                <TabPane className="fullWitdthHeight" tabId="1" role="tabpanel" id="acct-pane-account" aria-labelledby="acct-tab-account">
                  <div className="cn-acct-head">
                    <div>
                      <h2 className="cn-panel-title">Account</h2>
                      <p className="cn-panel-sub">Your name as it appears on the site.</p>
                    </div>
                    <button className="cn-btn-ghost cn-btn-dark" type="button" onClick={hangleEdit}>
                      Edit
                    </button>
                  </div>

                  <form onSubmit={handleSubmit} role="form" className="cn-acct-form">
                    <div className="cn-field">
                      <label className="cn-label-light" htmlFor="acct-name">Name</label>
                      <input
                        id="acct-name"
                        className="cn-input-light"
                        name="name"
                        type="text"
                        autoComplete="given-name"
                        value={state.name}
                        onChange={handleChange}
                        disabled={!allowEdit}
                      />
                    </div>

                    <div className="cn-field cn-field-last">
                      <label className="cn-label-light" htmlFor="acct-last">Last</label>
                      <input
                        id="acct-last"
                        className="cn-input-light"
                        name="last"
                        type="text"
                        autoComplete="family-name"
                        value={state.last}
                        onChange={handleChange}
                        disabled={!allowEdit}
                      />
                    </div>

                    <p className="cn-acct-note">
                      Your email address and password live under{" "}
                      <a
                        href="#security"
                        onClick={(e) => { e.preventDefault(); toggle("3"); }}
                      >
                        Security
                      </a>.
                    </p>

                    {accountError && <div className="cn-error-light" role="alert">{accountError}</div>}
                    {accountMessage && <div className="cn-success-light" role="status">{accountMessage}</div>}

                    <button className="cn-btn-primary" type="submit" disabled={!allowEdit}>
                      Save
                    </button>
                  </form>
                </TabPane>

                {/* ---------------------------- Subscription -------------- */}
                <TabPane tabId="2" role="tabpanel" id="acct-pane-subscription" aria-labelledby="acct-tab-subscription">
                  <div className="cn-acct-head">
                    <div>
                      {/* tabIndex -1: focusable from code (onGoToBilling), not a tab stop. */}
                      <h2 className="cn-panel-title" ref={subscriptionTitleRef} tabIndex={-1}>{t("accountSettings.subscription.title")}</h2>
                      <p className="cn-panel-sub">{t("accountSettings.subscription.sub")}</p>
                    </div>
                  </div>

                  {/* The Studio plan first: it works without Studio access,
                      so a plan can always be seen, changed and cancelled. */}
                  <StudioPlanPanel />

                  <h3 className="cn-panel-title" style={{ fontSize: "1.15rem", marginTop: 32 }}>
                    {t("accountSettings.subscription.coursesTitle")}
                  </h3>
                  {haveSub ? (
                    <>
                      <div className="cn-acct-facts">
                        <div className="cn-acct-fact">
                          <span className="cn-acct-fact-label">{t("accountSettings.subscription.plan")}</span>
                          <span className="cn-acct-fact-value">
                            {subInfo ? subInfo.plan.name : "—"}
                          </span>
                        </div>
                        <div className="cn-acct-fact">
                          <span className="cn-acct-fact-label">{t("accountSettings.subscription.status")}</span>
                          <span className="cn-acct-fact-value">
                            {subInfo ? subInfo.status : "—"}
                          </span>
                        </div>
                      </div>
                      <button
                        className="cn-btn-primary"
                        type="button"
                        onClick={() => requestUserPortal()}
                      >
                        {t("accountSettings.subscription.manageSubscription")}
                      </button>
                    </>
                  ) : subInfo?.hasCustomer ? (
                    // No course plan, but a Stripe customer: a Studio hosting
                    // plan, credits or past purchases. They still need a way to
                    // change their card and see invoices. (Hosting plans are
                    // cancelled from each app's Publish tab.)
                    <>
                      <p className="cn-panel-sub">{t("accountSettings.subscription.billingHelp")}</p>
                      <button
                        className="cn-btn-primary"
                        type="button"
                        onClick={() => requestUserPortal()}
                      >
                        {t("accountSettings.subscription.manageBilling")}
                      </button>
                    </>
                  ) : (
                    <button
                      className="cn-btn-primary"
                      type="button"
                      onClick={() => navigate("/subscriptions")}
                    >
                      {t("accountSettings.subscription.goToSubscriptions")}
                    </button>
                  )}

                  {hostingPlans.length > 0 && (
                    <section style={{ marginTop: 32 }} aria-labelledby="hosting-plans-title">
                      <h3 id="hosting-plans-title" className="cn-panel-title" style={{ fontSize: "1.15rem" }}>
                        {t("accountSettings.subscription.hostingTitle")}
                      </h3>
                      <p className="cn-panel-sub">{t("accountSettings.subscription.hostingHelp")}</p>
                      {hostingPlans.map((plan) => (
                        <div key={plan.projectId} style={{ marginTop: 16 }}>
                          <h4 style={{ fontSize: "1rem", marginBottom: 6 }}>{plan.name}</h4>
                          <StudioHostingPanel projectId={plan.projectId} hostingStatus={plan.status} />
                        </div>
                      ))}
                    </section>
                  )}
                </TabPane>

                {/* ------------------------------- Security --------------- */}
                <TabPane className="fullWitdthHeight2" tabId="3" role="tabpanel" id="acct-pane-security" aria-labelledby="acct-tab-security">
                  <div className="cn-acct-head">
                    <div>
                      <h2 className="cn-panel-title">Email address</h2>
                      <p className="cn-panel-sub">
                        You log in with this address. Currently <strong>{user?.email}</strong>.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handleEmailSubmit} role="form" className="cn-acct-form">
                    <div className="cn-field">
                      <label className="cn-label-light" htmlFor="acct-new-email">
                        New email address
                      </label>
                      <input
                        id="acct-new-email"
                        className="cn-input-light"
                        name="newEmail"
                        type="email"
                        value={emailForm.newEmail}
                        autoComplete="off"
                        onChange={handleEmailChange}
                      />
                    </div>

                    <div className="cn-field cn-field-last">
                      <label className="cn-label-light" htmlFor="acct-email-current-pw">
                        Current password
                      </label>
                      <input
                        id="acct-email-current-pw"
                        className="cn-input-light"
                        name="currentPassword"
                        type="password"
                        value={emailForm.currentPassword}
                        autoComplete="current-password"
                        onChange={handleEmailChange}
                      />
                    </div>

                    {emailError && <div className="cn-error-light" role="alert">{emailError}</div>}
                    {emailMessage && <div className="cn-success-light" role="status">{emailMessage}</div>}

                    <button className="cn-btn-primary" type="submit" disabled={emailSaving}>
                      {emailSaving ? "Updating..." : "Update email"}
                    </button>
                  </form>

                  <hr className="cn-panel-rule" />

                  <div className="cn-acct-head">
                    <div>
                      <h2 className="cn-panel-title">Password</h2>
                      <p className="cn-panel-sub">
                        Use at least 6 characters. You will stay logged in on this device.
                      </p>
                    </div>
                  </div>

                  <form onSubmit={handlePasswordSubmit} role="form" className="cn-acct-form">
                    <div className="cn-field">
                      <label className="cn-label-light" htmlFor="acct-pw-current">
                        Current password
                      </label>
                      <input
                        id="acct-pw-current"
                        className="cn-input-light"
                        name="currentPassword"
                        type="password"
                        value={passwordForm.currentPassword}
                        autoComplete="current-password"
                        onChange={handlePasswordChange}
                      />
                    </div>

                    <div className="cn-field">
                      <label className="cn-label-light" htmlFor="acct-pw-new">
                        New password
                      </label>
                      <input
                        id="acct-pw-new"
                        className="cn-input-light"
                        name="newPassword"
                        type="password"
                        value={passwordForm.newPassword}
                        autoComplete="new-password"
                        onChange={handlePasswordChange}
                      />
                    </div>

                    <div className="cn-field cn-field-last">
                      <label className="cn-label-light" htmlFor="acct-pw-confirm">
                        Confirm new password
                      </label>
                      <input
                        id="acct-pw-confirm"
                        className="cn-input-light"
                        name="confirmPassword"
                        type="password"
                        value={passwordForm.confirmPassword}
                        autoComplete="new-password"
                        onChange={handlePasswordChange}
                      />
                    </div>

                    {passwordError && <div className="cn-error-light" role="alert">{passwordError}</div>}
                    {passwordMessage && <div className="cn-success-light" role="status">{passwordMessage}</div>}

                    <button className="cn-btn-primary" type="submit" disabled={passwordSaving}>
                      {passwordSaving ? "Updating..." : "Update password"}
                    </button>

                    <p className="cn-acct-note mt-3 mb-0">
                      <Link to="/forgot-password">Forgot your current password?</Link>
                    </p>
                  </form>

                  {/* Last on the tab, after its own rule, so it is never the
                      first thing someone reaches for. Renders nothing for
                      guests. A plan that is still billing blocks deletion, so
                      the section can send people to the Subscription tab to
                      cancel it; scrolled to the top, because that tab is short
                      and this section sits far down the page. The button that
                      was clicked is inside this pane, which is now hidden, so
                      focus would drop to <body>: move it to the Subscription
                      heading once that pane is on screen (the click's render
                      has been committed by the next frame). */}
                  <DeleteAccountSection
                    onGoToBilling={() => {
                      toggle('2');
                      window.scrollTo(0, 0);
                      window.requestAnimationFrame(() => subscriptionTitleRef.current?.focus());
                    }}
                  />
                </TabPane>

                {/* --------------------------- Desktop credits ------------ */}
                {/* Credits pay for model calls the Codenec desktop app makes
                    on Codenec's AI account. Buying them is a WEB job on
                    purpose: the app holds no card details, no Stripe session
                    and nothing worth phishing, and the account that pays is
                    the account the app is already signed in to.

                    Nothing here reads Firestore. The balance lives in
                    desktop_credits/<uid>, which has no client-SDK rule at all
                    — a client-readable balance is a client-WRITABLE balance
                    one rule mistake later. */}
                {SHOW_DESKTOP_CREDITS && (
                <TabPane tabId="4" role="tabpanel" id="acct-pane-credits" aria-labelledby="acct-tab-credits">
                  <div className="cn-acct-head">
                    <div>
                      <h2 className="cn-panel-title">Desktop credits</h2>
                      <p className="cn-panel-sub">
                        Credits pay for Codenec Desktop when it runs on Codenec&rsquo;s AI
                        account instead of your own API key.
                      </p>
                    </div>
                  </div>

                  {creditsLoading && (
                    <p className="cn-acct-note" role="status">Loading your balance&hellip;</p>
                  )}

                  {/* A failed refresh is not a reason to take the Buy buttons
                      away from a page that already loaded, so this is a notice
                      above the tab rather than a replacement for it. The retry
                      is here because the message promises one — telling
                      somebody to try again and giving them nothing to press
                      leaves reloading the page as the only way to do it. */}
                  {creditsError && (
                    <div className="cn-error-light" role="alert">
                      {creditsError}{" "}
                      <button
                        className="cn-link-button"
                        type="button"
                        onClick={loadCredits}
                        disabled={creditsLoading}
                      >
                        Try again
                      </button>
                    </div>
                  )}

                  {searchParams.get("credits") === "canceled" && (
                    <p className="cn-acct-note" role="status">
                      That purchase was cancelled. Nothing was charged.
                    </p>
                  )}

                  {credits && (
                    <>
                      <div className="cn-acct-facts">
                        <div className="cn-acct-fact">
                          <span className="cn-acct-fact-label">Balance</span>
                          <span className="cn-acct-fact-value">
                            {formatCredits(credits.balance)}
                          </span>
                        </div>
                        <div className="cn-acct-fact">
                          <span className="cn-acct-fact-label">Status</span>
                          <span className="cn-acct-fact-value">
                            {credits.balance <= 0
                              ? "Out of credits"
                              : credits.balance <= credits.lowWater
                                ? "Running low"
                                : "Ready"}
                          </span>
                        </div>
                      </div>

                      {/* The eligibility answer, in words a person can act on.
                          The server decides it from the account role; this
                          page only reports it. Hiding the packs without saying
                          why would leave someone clicking nothing. */}
                      {!credits.eligible ? (
                        <p className="cn-acct-note">
                          Codenec credits are for desktop app clients. If you are using
                          Codenec Desktop, <Link to="/contact">contact us</Link> and we
                          will set your account up.
                        </p>
                      ) : (
                        <>
                          <div className="cn-credit-packs">
                            {credits.packs.map((pack) => (
                              <div className="cn-credit-pack" key={pack.id}>
                                <span className="cn-credit-pack-price">{formatPlanPrice({ amount: Math.round((Number(pack.priceUsd) || 0) * 100), currency: "usd" })}</span>
                                <span className="cn-credit-pack-credits">
                                  {formatCredits(pack.credits)} of credits
                                </span>
                                <button
                                  className="cn-btn-primary"
                                  type="button"
                                  /* A pack whose Stripe price id is unset is
                                     disabled rather than hidden: the gap is a
                                     deploy that forgot an env var, and a
                                     silently missing pack hides that bug. */
                                  disabled={!pack.available || Boolean(buyingPack)}
                                  onClick={() => buyCreditPack(pack.id)}
                                >
                                  {buyingPack === pack.id
                                    ? "Opening checkout…"
                                    : pack.available
                                      ? "Buy"
                                      : "Unavailable"}
                                </button>
                              </div>
                            ))}
                          </div>

                          <p className="cn-acct-note">
                            One-time payment, no subscription. Credits do not expire, and
                            they are spent only while the desktop app is set to
                            &ldquo;Codenec credits&rdquo; &mdash; on your own API key they
                            are not touched at all.
                          </p>
                        </>
                      )}

                      {credits.purchases.length > 0 && (
                        <>
                          <hr className="cn-panel-rule" />
                          <div className="cn-acct-head">
                            <div>
                              <h2 className="cn-panel-title">Recent top-ups</h2>
                              {/* Listed from the moment credits LANDED, not
                                  from Stripe. A payment that was taken but
                                  never granted must not appear here as though
                                  it had — that is the case worth noticing. */}
                              <p className="cn-panel-sub">Credits added to this account.</p>
                            </div>
                          </div>
                          <ul className="cn-credit-history">
                            {credits.purchases.map((purchase) => (
                              <li key={purchase.id}>
                                <span className="cn-credit-history-date">
                                  {purchase.createdAt
                                    ? new Date(purchase.createdAt).toLocaleDateString()
                                    : "—"}
                                </span>
                                <span className="cn-credit-history-credits">
                                  +{formatCredits(purchase.credits)}
                                </span>
                                <span className="cn-credit-history-paid">
                                  {formatMoney(purchase.amountTotal, purchase.currency)}
                                </span>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </>
                  )}
                </TabPane>
                )}

              </TabContent>
            </div>
          </div>
        </Container>
      </main>
      <MainFooter />
    </>
  );

}

export default AccountSettingsComponent;
